import { useState, useEffect, type FC } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import type { CreateInvitationDto, SurveyInvitation } from '@/types';
import { invitationService } from '@/services/invitationService';
import { getInvitationUrl } from '@/utils/surveyAccessMode';
import { toast } from 'sonner';

// Validation schema
const createInvitationSchema = z.object({
  emails: z.array(z.string().email('Invalid email address')).optional(),
  maxUses: z
    .union([
      z.number().int('Must be a whole number').positive('Must be at least 1'),
      z.null(),
      z.undefined(),
    ])
    .optional()
    .nullable(),
  expiresDate: z.date().min(new Date(), 'Expiration date must be in the future').optional().nullable(),
});

type CreateInvitationFormData = z.infer<typeof createInvitationSchema>;

interface CreateInvitationDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback when dialog should close */
  onClose: () => void;

  /** Survey ID to create invitation for */
  surveyId: number;

  /** Callback when invitation is successfully created */
  onInvitationCreated: (invitation: SurveyInvitation) => void;
}

/**
 * CreateInvitationDialog Component
 *
 * Modal dialog for creating a new survey invitation.
 *
 * Features:
 * - Optional email field (valid email format)
 * - Optional max uses field (positive integer)
 * - Optional expiration date (future date)
 * - Form validation with Zod
 * - Success state showing generated URL
 * - Copy URL button with feedback
 * - Loading state during API call
 * - Error handling
 */
const CreateInvitationDialog: FC<CreateInvitationDialogProps> = ({
  open,
  onClose,
  surveyId,
  onInvitationCreated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [createdInvitation, setCreatedInvitation] = useState<SurveyInvitation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState('');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateInvitationFormData>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: {
      emails: [],
      maxUses: null,
      expiresDate: null,
    },
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCreatedInvitation(null);
      setEmailInputValue('');
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setCreatedInvitation(null);
      setEmailInputValue('');
      onClose();
    }
  };

  const handleDone = () => {
    // Close both the success dialog and the main create dialog
    reset();
    setCreatedInvitation(null);
    setEmailInputValue('');
    onClose();
  };

  const onSubmit = async (data: CreateInvitationFormData) => {
    setIsSubmitting(true);

    try {
      const emails = data.emails && data.emails.length > 0 ? data.emails : [undefined];
      const createdInvitations: SurveyInvitation[] = [];

      // Create invitation for each email (or one without email if array is empty)
      for (const email of emails) {
        const dto: CreateInvitationDto = {
          email: email || undefined,
          maxUses: data.maxUses ?? undefined,
          expiresDate: data.expiresDate?.toISOString(),
        };

        const invitation = await invitationService.createInvitation(surveyId, dto);
        createdInvitations.push(invitation);
        onInvitationCreated(invitation);
      }

      // Show the first created invitation in success state
      setCreatedInvitation(createdInvitations[0]);

      if (createdInvitations.length === 1) {
        toast.success('Invitation created successfully');
      } else {
        toast.success(`${createdInvitations.length} invitations created successfully`);
      }
    } catch (error) {
      toast.error('Failed to create invitation');
      console.error('Failed to create invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyUrl = async () => {
    if (createdInvitation) {
      try {
        const url = getInvitationUrl(createdInvitation.token);
        await navigator.clipboard.writeText(url);
        toast.success('Invitation link copied to clipboard');
      } catch {
        toast.error('Failed to copy link');
      }
    }
  };

  // If invitation was created, show success state
  if (createdInvitation) {
    const invitationUrl = getInvitationUrl(createdInvitation.token);

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" sx={{ pr: 5, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Invitation Created
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: { xs: 4, sm: 8 },
              top: { xs: 4, sm: 8 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your invitation has been created successfully!
          </Alert>

          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            Invitation Link:
          </Typography>
          <TextField
            value={invitationUrl}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyUrl}
                    edge="end"
                    sx={{
                      minWidth: { xs: 44, sm: 40 },
                      minHeight: { xs: 44, sm: 40 },
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              },
            }}
          />

          {createdInvitation.email && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 2,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                wordBreak: 'break-word',
              }}
            >
              Email: {createdInvitation.email}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={handleCopyUrl}
            startIcon={<ContentCopyIcon />}
            sx={{
              minHeight: { xs: 44, sm: 36 },
            }}
          >
            Copy Link
          </Button>
          <Button
            onClick={handleDone}
            variant="contained"
            sx={{
              minHeight: { xs: 44, sm: 36 },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Show creation form
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ pr: 5, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Create Invitation
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: { xs: 4, sm: 8 },
            top: { xs: 4, sm: 8 },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              Create a unique invitation link to share your survey. Add emails to track who you invited, set usage limits, or add an expiration date. All fields are optional.
            </Typography>

            {/* Multiple Emails Field */}
            <Controller
              name="emails"
              control={control}
              render={({ field }) => {
                const validateAndAddEmail = (newValue: string[]) => {
                  // Process each new value to handle comma/space separated emails
                  const processedEmails: string[] = [];

                  newValue.forEach(value => {
                    // Split by comma, semicolon, or space
                    const splitEmails = value.split(/[,;\s]+/).filter(e => e.trim());

                    splitEmails.forEach(email => {
                      const trimmedEmail = email.trim();
                      // Improved email validation - requires at least 2 chars for domain and TLD
                      const emailRegex = /^[^\s@]+@[^\s@]{2,}\.[^\s@]{2,}$/;
                      if (trimmedEmail && emailRegex.test(trimmedEmail)) {
                        // Avoid duplicates
                        if (!processedEmails.includes(trimmedEmail)) {
                          processedEmails.push(trimmedEmail);
                        }
                      } else if (trimmedEmail) {
                        // Show error for invalid email
                        toast.error(`Invalid email: ${trimmedEmail}`);
                      }
                    });
                  });

                  return processedEmails;
                };

                // Handle adding email when user clicks outside (blur event)
                const handleBlur = () => {
                  const trimmedInput = emailInputValue.trim();
                  if (trimmedInput) {
                    // Split by comma, semicolon, or space to handle multiple emails
                    const splitEmails = trimmedInput.split(/[,;\s]+/).filter(e => e.trim());
                    const currentEmails = field.value || [];
                    // Improved email validation - requires at least 2 chars for domain and TLD
                    const emailRegex = /^[^\s@]+@[^\s@]{2,}\.[^\s@]{2,}$/;
                    let addedCount = 0;
                    let hasInvalidEmail = false;

                    splitEmails.forEach(email => {
                      const trimmedEmail = email.trim();
                      if (trimmedEmail && emailRegex.test(trimmedEmail)) {
                        // Add to emails array if not already present
                        if (!currentEmails.includes(trimmedEmail)) {
                          currentEmails.push(trimmedEmail);
                          addedCount++;
                        }
                      } else if (trimmedEmail) {
                        // Show error for invalid email
                        toast.error(`Invalid email: ${trimmedEmail}`);
                        hasInvalidEmail = true;
                      }
                    });

                    if (addedCount > 0) {
                      field.onChange([...currentEmails]);
                    }

                    // Only clear the input field if all emails were valid and added
                    // If there's an invalid email, keep it in the input so user can edit
                    if (!hasInvalidEmail && addedCount > 0) {
                      setEmailInputValue('');
                    }
                  }
                };

                return (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    inputValue={emailInputValue}
                    onInputChange={(_, newInputValue) => {
                      setEmailInputValue(newInputValue);
                    }}
                    onChange={(_, newValue) => {
                      const validEmails = validateAndAddEmail(newValue);
                      field.onChange(validEmails);
                    }}
                    onBlur={handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Emails (Optional)"
                        placeholder="Type email and press Enter or click outside"
                        error={!!errors.emails}
                        helperText={
                          errors.emails?.message ||
                          'Press Enter or click outside to add each email. Separate multiple with comma or space. Leave empty for open invitation.'
                        }
                      />
                    )}
                    slotProps={{
                      chip: {
                        variant: 'outlined',
                        color: 'primary',
                      },
                    }}
                    disabled={isSubmitting}
                  />
                );
              }}
            />

            {/* Max Uses Field */}
            <TextField
              {...register('maxUses', {
                setValueAs: (v) => {
                  if (v === '' || v === null || v === undefined) return null;
                  const num = Number(v);
                  return isNaN(num) ? null : num;
                },
              })}
              label="Max Uses (Optional)"
              type="number"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              error={!!errors.maxUses}
              helperText={errors.maxUses?.message || 'Maximum number of times this link can be used (leave empty for unlimited)'}
              disabled={isSubmitting}
            />

            {/* Expiration Date Field */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="expiresDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue ? newValue.toDate() : null);
                    }}
                    label="Expiration Date (Optional)"
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.expiresDate,
                        helperText: errors.expiresDate?.message || 'Date when this invitation link will stop working (leave empty for no expiration)',
                      },
                    }}
                    disabled={isSubmitting}
                  />
                )}
              />
            </LocalizationProvider>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              minHeight: { xs: 44, sm: 36 },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              minHeight: { xs: 44, sm: 36 },
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateInvitationDialog;
